import { useState, useCallback } from "react";
import { ncdAPI } from "../../../services/ncd.api";

const MEDICAL_ROLES = ["MO", "DEO", "STAFF_NURSE"];

export const useFacilityDetails = ({
  familyInfo,
  fetchDistricts,
  fetchTalukas,
  setVillages,
}) => {
  const [formDetails, setformDetails] = useState(null);
  const [subusers, setsubUsers] = useState([]);
  const [ashas, setAshas] = useState([]);
  const [phcs, setPHCs] = useState([]);
  const [subcenters, setSubcenters] = useState([]);

  const updateVillageAndUsers = useCallback(async (subcenterId, villageId) => {
    if (!subcenterId) return;

    try {
      const [villResp, userResp, villageResp] = await Promise.all([
        ncdAPI.getVillages(subcenterId),
        ncdAPI.getSubcenterUsers(subcenterId),
        ncdAPI.getVillageData(subcenterId, villageId),
      ]);

      setVillages(villResp?.data?.villages || []);
      setsubUsers(userResp?.data || []);
      setAshas(villageResp?.data || villageResp || []);
    } catch (error) {
      console.error("Error updating village and users:", error);
    }
  }, [setVillages]);

  const handleLocationHierarchy = useCallback(async (hierarchy) => {
    if (!hierarchy) return;

    try {
      if (hierarchy.stateId) await fetchDistricts(hierarchy.stateId);
      if (hierarchy.districtId) await fetchTalukas(hierarchy.districtId);

      if (hierarchy.talukaId) {
        const talukaChildren = await ncdAPI.getTalukaChildren(hierarchy.talukaId);
        setPHCs(talukaChildren?.data || talukaChildren || []);
      }
    } catch (error) {
      console.error("Error handling location hierarchy:", error);
    }
  }, [fetchDistricts, fetchTalukas]);

  const getDetails = useCallback(async (facilityTypeId, facility, facilityType, rolename) => {
    try {
      const isMedicalRole = MEDICAL_ROLES.includes(rolename);

      if (isMedicalRole && facilityType === "PHC") {
        const res = await ncdAPI.getFacilityDetails(facilityTypeId, facility);
        const data = res.data;

        setformDetails(data);
        await handleLocationHierarchy(data?.locationHierarchy);

        const phcChildren = await ncdAPI.getPHCChildren(facility);
        setSubcenters(phcChildren?.data || []);

        const addr = familyInfo?.addressInfo;
        if (addr?.subcenterId) {
          await updateVillageAndUsers(addr.subcenterId, addr.villageId);
        }
      } else if (isMedicalRole && facilityType !== "PHC") {
        // Handle DEO/MO/STAFF_NURSE with non-PHC facility
        const addr = familyInfo?.addressInfo;
        
        if (addr) {
          await handleLocationHierarchy(addr);
          
          if (addr.phcId) {
            const phcChildren = await ncdAPI.getPHCChildren(addr.phcId);
            setSubcenters(phcChildren?.data || []);
          }
          
          if (addr.subcenterId) {
            await updateVillageAndUsers(addr.subcenterId, addr.villageId);
          }
        }
      } else {
        const [response, userResp] = await Promise.all([
          ncdAPI.getVillages(facility),
          ncdAPI.getSubcenterUsers(facility),
        ]);

        if (!response?.data) return;

        const data = response.data;

        setformDetails(data);
        setVillages(data?.villages || []);
        setsubUsers(userResp?.data || []);

        await handleLocationHierarchy(data);

        const addr = familyInfo?.addressInfo;
        if (addr?.subcenterId) {
          await updateVillageAndUsers(addr.subcenterId, addr.villageId);
        }
      }
    } catch (error) {
      console.error("Error getting facility details:", error);
    }
  }, [familyInfo, handleLocationHierarchy, updateVillageAndUsers, setVillages]);

  return {
    formDetails,
    subusers,
    ashas,
    phcs,
    subcenters,
    getDetails,
  };
};